import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { Employee, Role } from '@lms/shared-types'

import { setEmployeeRoles } from '../../api/admin-roles'
import { listEmployeesPage } from '../../api/employees'
import { Badge } from '../../components/primitives/Badge'
import { Banner } from '../../components/primitives/Banner'
import { Button } from '../../components/primitives/Button'
import { roleLabel } from '../../lib/role'

function rolesOf(e: Employee): Role[] {
  return e.roles && e.roles.length > 0 ? e.roles : [e.role]
}

/** List employees and toggle the `admin` role on/off (preserving their other
 *  roles). This is the in-app "admin accounts" surface — an admin mints another
 *  admin. Full multi-role editing is a follow-up; this covers the core need.
 *
 *  Note: the employee list is RLS-backed (HR+ data rank). A pure admin without
 *  a business role won't see rows — the seeded admin also holds exec_director.
 *  A dedicated admin-list endpoint is a follow-up for pure-admin deployments. */
export function AdminUsersTab(): JSX.Element {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['admin', 'employees', 'page0'],
    queryFn: () => listEmployeesPage({ limit: 50 }),
  })

  const mutation = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: Role[] }) =>
      setEmployeeRoles(id, roles),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] })
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Failed to update roles')
    },
  })

  function toggleAdmin(e: Employee): void {
    const current = rolesOf(e)
    const has = current.includes('admin')
    const next: Role[] = has
      ? current.filter((r) => r !== 'admin')
      : [...current, 'admin']
    mutation.mutate({ id: e.id, roles: next })
  }

  if (query.isLoading) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>
  if (query.isError) {
    return (
      <Banner
        kind="escalation"
        title="Couldn't load employees"
        subtitle="Your account may lack data-view access. Admin role management needs a data-viewing role on this deployment."
      />
    )
  }

  const employees = query.data?.data ?? []

  return (
    <div style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
      {error ? <Banner kind="escalation" title="Update failed" subtitle={error} /> : null}
      {employees.map((e) => {
        const roles = rolesOf(e)
        const isAdmin = roles.includes('admin') || roles.includes('superadmin')
        return (
          <div
            key={e.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: 'var(--surface)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>{e.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.email}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {roles.map((r) => (
                <Badge key={r} variant={r === 'admin' || r === 'superadmin' ? 'info' : 'default'}>
                  {roleLabel(r)}
                </Badge>
              ))}
              {roles.includes('superadmin') ? null : (
                <Button
                  variant="soft"
                  size="sm"
                  disabled={mutation.isPending}
                  onClick={() => toggleAdmin(e)}
                >
                  {isAdmin ? 'Remove admin' : 'Make admin'}
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
